import { Keypair } from "@solana/web3.js";
import * as bip39 from "bip39";
import * as ed25519_hd_key from "ed25519-hd-key";
import Config from "../../config/settings";
import UserService from "../../services/user";

export interface PrioritizationFeeObject {
    slot: number;
    prioritizationFee: number;
}

export class WalletService {

  baseUrl: string;

  constructor() {
    this.baseUrl = (Config.debugMode === false) ? Config.url : Config.urlDebug;
  }

  getSolanaTokenAccountsByOwner(splKey: string, userService: UserService): Promise<Response> {
    const url = this.baseUrl + 'get-solana-token-account-by-owner?pubkey=' + userService.pubkey + '&splkey=' + splKey+ '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  async getOurTokenAccountsByOwner(userService: UserService) {
    const url = this.baseUrl + 'get-our-token-account-by-owner?accountpubkey=' + userService.pubkey + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  getSolanaBalance(userService: UserService): Promise<Response> {
    const url = this.baseUrl + 'get-balance?pubkey=' + userService.pubkey + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  getRecentPrioritizationFees(userService: UserService): Promise<Response> {
    const url = this.baseUrl + 'get-recent-prioritization-fees?email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  async getLatestBlockhash(commitment: string = 'confirmed', userService: UserService) {
    const url = this.baseUrl + 'get-latest-blockhash?commitment=' + commitment + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  getTokenInfoPyth(mint: string, userService: UserService) {
    const url = this.baseUrl + 'get-spl-token-data?mint=' + mint + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  createTokenAsset(mint: string, seed: string, priorityFee: number, userService: UserService) {
    const url = this.baseUrl + 'create-token-asset?mint=' + mint + '&seed=' + seed + '&priorityFee=' + priorityFee + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  getParsedAccountInfo(commitment: string = 'finalized', account: string, userService: UserService) {
    const url = this.baseUrl + 'get-account-info?commitment=' + commitment + '&account=' + account + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  transferSplToken(mint: string, amount: number, destination: string, seed: string, priorityFee: number, userService: UserService) {
    const seedBip39 = bip39.mnemonicToSeedSync(seed, "");       
    const seedHex = Buffer.from(seedBip39.slice(0, 32)).toString('hex');  
    const url = this.baseUrl + 'transfer-spl-token?mint=' + mint + '&amount=' + amount + '&destination=' + destination + '&seed=' + seedHex + '&priorityFee=' + priorityFee + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  transferSolToken(mint: string, amount: number, destination: string, seed: string, priorityFee: number, userService: UserService) {
    const seedBip39 = bip39.mnemonicToSeedSync(seed, "");       
    const seedHex = Buffer.from(seedBip39.slice(0, 32)).toString('hex');  
    const url = this.baseUrl + 'transfer-sol-token?mint=' + mint + '&amount=' + amount + '&destination=' + destination + '&seed=' + seedHex + '&priorityFee=' + priorityFee + '&email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  /**
   * Calcul des frais de transfert
   */
  public async calculateFeeTransfer(userService: UserService) {
    const url = this.baseUrl + 'calculate-fee-transfer?email=' + userService.email + '&security=' + userService.security + '&v=' + Config.version;
    return fetch(url, { signal: AbortSignal.timeout(15000) });
  }

  /**
   * Génération du wallet
   * @param mnemonic suite de mots clés 
   */
  generateWallet(mnemonic: string) {
    let wallet: any = {};
    let master_seed = bip39.mnemonicToSeedSync(mnemonic);
    let index = 0;
    let derived_path = "m/44'/501'/" + index + "'/0'";
    let derived_seed = ed25519_hd_key.derivePath(derived_path, master_seed.toString('hex')).key;
    wallet.keypair = Keypair.fromSeed(new Uint8Array(derived_seed));
    wallet.mnemonic = mnemonic;
    wallet.publicAddress = wallet.keypair.publicKey.toBase58();
    return wallet;
  }

  /**
   * Génération de mots clés de wallet
   */
  generateMnemonic(): string {
    return bip39.generateMnemonic();
  }
}